import { useEffect, useState, useRef, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { UserRole, roleService } from "@/lib/services/role.service";
import { toast } from "sonner";

interface UseRoleAccessResult {
  hasAccess: boolean;
  isLoading: boolean;
  redirectPath: string | null;
}

interface CacheEntry {
  hasAccess: boolean;
  redirectPath: string | null;
  timestamp: number;
}

const routeCache = new Map<string, CacheEntry>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export function useRoleAccessOptimized(allowedRoles: UserRole[]): UseRoleAccessResult {
  const { user, isLoading: authLoading } = useAuth();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [redirectPath, setRedirectPath] = useState<string | null>(null);
  const checkingRef = useRef(false);

  const cacheKey = useMemo(() => {
    if (!user) return null;
    return `${user.id}-${location.pathname}-${allowedRoles.join(",")}`;
  }, [user?.id, location.pathname, allowedRoles.join(",")]);

  useEffect(() => {
    if (checkingRef.current || authLoading) return;

    let isMounted = true;

    const checkAccess = async () => {
      // Check cache first
      if (cacheKey) {
        const cached = routeCache.get(cacheKey);
        if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
          if (isMounted) {
            setHasAccess(cached.hasAccess);
            setRedirectPath(cached.redirectPath);
            setIsLoading(false);
          }
          return;
        }
      }

      checkingRef.current = true;

      try {
        // No user = redirect to login
        if (!user) {
          if (isMounted) {
            setHasAccess(false);
            setRedirectPath("/auth/login");
            setIsLoading(false);
          }
          return;
        }

        // Quick check: if route accepts all authenticated users
        if (
          allowedRoles.length === 3 &&
          allowedRoles.includes("renter") &&
          allowedRoles.includes("owner") &&
          allowedRoles.includes("admin")
        ) {
          if (isMounted) {
            setHasAccess(true);
            setIsLoading(false);
            if (cacheKey) {
              routeCache.set(cacheKey, {
                hasAccess: true,
                redirectPath: null,
                timestamp: Date.now(),
              });
            }
          }
          return;
        }

        // Check role (parallel with validation if needed, but role is most critical for redirect)
        const userRole = await roleService.getUserRole(user.id);
        const isEmailVerified = user.email_confirmed_at != null;

        // Check email verification
        if (!isEmailVerified) {
          if (isMounted) {
            setHasAccess(false);
            setRedirectPath("/auth/verify-email");
            setIsLoading(false);
            if (cacheKey) {
              routeCache.set(cacheKey, {
                hasAccess: false,
                redirectPath: "/auth/verify-email",
                timestamp: Date.now(),
              });
            }
          }
          return;
        }

        // Check role
        if (!userRole || !allowedRoles.includes(userRole)) {
          const isAdminOnlyRoute =
            allowedRoles.length === 1 && allowedRoles[0] === "admin";
          const redirect = isAdminOnlyRoute
            ? "/"
            : userRole === "owner"
              ? "/dashboard/owner"
              : "/dashboard/renter";

          if (isMounted) {
            setHasAccess(false);
            setRedirectPath(redirect);
            setIsLoading(false);
            if (cacheKey) {
              routeCache.set(cacheKey, {
                hasAccess: false,
                redirectPath: redirect,
                timestamp: Date.now(),
              });
            }

            if (!isAdminOnlyRoute) {
              toast.error(
                `Accès non autorisé. Redirection vers votre tableau de bord ${userRole === "owner" ? "propriétaire" : "locataire"}`
              );
            }
          }
          return;
        }

        // For owner-only routes, allow access (verification banner will handle UI)
        if (allowedRoles.length === 1 && allowedRoles[0] === "owner") {
          if (isMounted) {
            setHasAccess(true);
            setIsLoading(false);
            if (cacheKey) {
              routeCache.set(cacheKey, {
                hasAccess: true,
                redirectPath: null,
                timestamp: Date.now(),
              });
            }
          }
          return;
        }

        // For renter-only routes, allow access (verification banner will handle UI)
        if (allowedRoles.length === 1 && allowedRoles[0] === "renter") {
          if (isMounted) {
            setHasAccess(true);
            setIsLoading(false);
            if (cacheKey) {
              routeCache.set(cacheKey, {
                hasAccess: true,
                redirectPath: null,
                timestamp: Date.now(),
              });
            }
          }
          return;
        }

        // Default: allow access
        if (isMounted) {
          setHasAccess(true);
          setIsLoading(false);
          if (cacheKey) {
            routeCache.set(cacheKey, {
              hasAccess: true,
              redirectPath: null,
              timestamp: Date.now(),
            });
          }
        }
      } catch (error) {
        console.error("Error in checkAccess:", error);
        if (isMounted) {
          setHasAccess(false);
          setRedirectPath("/auth/login");
          setIsLoading(false);
        }
      } finally {
        checkingRef.current = false;
      }
    };

    checkAccess();

    return () => {
      isMounted = false;
      checkingRef.current = false;
    };
  }, [user?.id, location.pathname, cacheKey, allowedRoles.join(","), authLoading]);

  return {
    hasAccess,
    isLoading: isLoading || authLoading,
    redirectPath,
  };
}
